import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

// Tile-type → emoji/style hints (frontend visual layer)
const TILE_TYPE_STYLES = {
  loot:         { border: "border-amber-400/60",   bg: "from-amber-900/40 to-amber-700/20" },
  mini_boss:    { border: "border-rose-500/70",    bg: "from-rose-900/50 to-rose-700/30" },
  shop:         { border: "border-indigo-400/60",  bg: "from-indigo-900/40 to-indigo-700/20" },
  bonus_roll:   { border: "border-sky-400/60",     bg: "from-sky-900/40 to-sky-700/20" },
  skip_forward: { border: "border-emerald-400/60", bg: "from-emerald-900/40 to-emerald-700/20" },
  skip_back:    { border: "border-zinc-500/60",    bg: "from-zinc-800 to-zinc-900" },
  stamp:        { border: "border-yellow-300",     bg: "from-yellow-700/50 to-yellow-500/30" },
  jackpot:      { border: "border-fuchsia-400/70", bg: "from-fuchsia-900/50 to-fuchsia-700/30" },
  branch:       { border: "border-cyan-300/70",    bg: "from-cyan-900/50 to-cyan-700/30" },
  gauntlet:     { border: "border-red-500/70",     bg: "from-red-900/60 to-red-700/30" },
  currency_x2:  { border: "border-lime-400/60",    bg: "from-lime-900/40 to-lime-700/20" },
  grand_prize:  { border: "border-amber-300",      bg: "from-amber-600/60 to-amber-400/40" },
  // 🏆 Phoenix Summit tile types
  mirror_match: { border: "border-red-500",        bg: "from-rose-900/70 to-red-700/40" },
  frost_slide:  { border: "border-cyan-300",       bg: "from-sky-900/60 to-cyan-700/30" },
  crown_jackpot:{ border: "border-yellow-300",     bg: "from-yellow-600/60 to-amber-400/40" },
  loot_box:     { border: "border-violet-400",     bg: "from-violet-900/50 to-purple-700/30" },
  summit:       { border: "border-amber-200",      bg: "from-amber-500/80 to-yellow-300/50" },
};

export default function FestivalBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(false);
  const [lastRoll, setLastRoll] = useState(null);
  const [branchPrompt, setBranchPrompt] = useState(null);
  const [fightPrompt, setFightPrompt] = useState(null);
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const fetchState = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/festival/state`, { withCredentials: true });
      setData(res.data);
      // Restore branch prompt from server state if any
      if (res.data?.state?.pending_branch) {
        setBranchPrompt(res.data.state.pending_branch);
      }
      // 🔥 Restore mirror-match fight modal if user reloaded mid-fight
      const stuck = res.data?.state?.stuck_on_tile;
      if (stuck && res.data?.board?.tiles) {
        const tile = res.data.board.tiles.find(t => t.id === stuck);
        if (tile && tile.type === "mirror_match") {
          setFightPrompt({
            tile_id: stuck,
            fight: {
              type: "mirror_match",
              icon: tile.icon,
              label: tile.label,
              enemy_stat_multiplier: tile.enemy_stat_multiplier || 1.10,
              must_win: true,
            },
            lost: true,
            rolls_remaining: res.data.state.free_rolls_remaining,
          });
        }
      }
    } catch (e) {
      console.warn("[festival] state fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  const handleRoll = async (paid = false) => {
    if (rolling) return;
    setRolling(true);
    setLastRoll(null);
    try {
      const res = await axios.post(`${API}/api/festival/roll`, { paid }, { withCredentials: true });
      setLastRoll(res.data);
      if (res.data.branch) {
        setBranchPrompt({ from: res.data.to, options: res.data.branch.options });
      }
      if (res.data.fight_required) {
        setFightPrompt({ tile_id: res.data.to, fight: res.data.fight });
      }
      // Toasts for clarity
      if (res.data.rewards?.length) {
        toast.success(`🎲 Rolled ${res.data.roll} · Tile ${res.data.to}`, {
          description: res.data.rewards.map(r => `${r.icon} ${r.name} ×${r.qty || 1}`).join(" · "),
        });
      } else {
        toast(`🎲 Rolled ${res.data.roll} · Tile ${res.data.to}`);
      }
      await fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Roll failed.");
    } finally {
      setRolling(false);
    }
  };

  const handlePickBranch = async (to) => {
    try {
      const res = await axios.post(`${API}/api/festival/branch`, { to }, { withCredentials: true });
      setBranchPrompt(null);
      setLastRoll((p) => ({ ...(p || {}), to, tile: res.data.tile, rewards: res.data.rewards }));
      if (res.data.fight_required) {
        setFightPrompt({ tile_id: to, fight: res.data.fight });
      }
      if (res.data.rewards?.length) {
        toast.success(`🌟 Took the fork → Tile ${to}`, {
          description: res.data.rewards.map(r => `${r.icon} ${r.name} ×${r.qty || 1}`).join(" · "),
        });
      }
      await fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Branch pick failed.");
    }
  };

  const handleFightResult = async (won) => {
    if (!fightPrompt) return;
    try {
      const res = await axios.post(`${API}/api/festival/fight-tile`,
        { tile_id: fightPrompt.tile_id, won },
        { withCredentials: true });
      // Mirror Match special handling:
      //  • Lost & stuck → keep modal open, show retry option
      //  • Won → close modal, celebrate
      if (res.data.mirror_match) {
        if (won) {
          setFightPrompt(null);
          if (res.data.rewards?.length) {
            toast.success("🔥 Mirror Match cleared!", {
              description: res.data.rewards.map(r => `${r.icon} ${r.name} ×${r.qty || 1}`).join(" · "),
            });
          }
        } else {
          // Stuck — keep modal open with a Retry prompt
          setFightPrompt((p) => ({ ...p, lost: true, rolls_remaining: res.data.rolls_remaining }));
          toast.warning(`💀 Defeated — 1 roll consumed. ${res.data.rolls_remaining} rolls left.`);
        }
        await fetchState();
        return;
      }
      // Non-mirror tile fight
      setFightPrompt(null);
      if (won && res.data.rewards?.length) {
        toast.success("⚔️ Victory!", {
          description: res.data.rewards.map(r => `${r.icon} ${r.name} ×${r.qty || 1}`).join(" · "),
        });
      } else if (!won) {
        toast.warning("💢 Fled the fight — no rewards.");
      }
      await fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fight resolution failed.");
    }
  };

  const handleFightBoss = async () => {
    try {
      // For MVP — auto-win the Festival Boss. Phase 2 will plug into combat engine.
      const res = await axios.post(`${API}/api/festival/fight-boss`,
        { won: true }, { withCredentials: true });
      toast.success(`🐺 ${board.boss.name} defeated!`, {
        description: (res.data.rewards || []).map(r => `${r.icon} ${r.name} ×${r.qty || 1}`).join(" · "),
      });
      await fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Boss fight failed.");
    }
  };

  const handleClaimGrand = async () => {
    try {
      const res = await axios.post(`${API}/api/festival/claim-grand-prize`,
        {}, { withCredentials: true });
      toast.success(`🏆 ${res.data.title || "Grand Prize Claimed!"}`, {
        description: (res.data.rewards || []).map(r => `${r.icon} ${r.name} ×${r.qty || 1}`).join(" · "),
      });
      await fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    }
  };

  // ─── Render guards ────────────────────────────────────────────────────
  if (loading) {
    return <div className="p-8 text-center text-zinc-400" data-testid="festival-board-loading">Loading festival…</div>;
  }
  if (!data?.active || !data?.board) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="festival-board-empty">
        <div className="text-6xl mb-4">🎆</div>
        <h2 className="text-2xl font-bold text-zinc-200 mb-2">No Active Festival Board</h2>
        <p className="text-zinc-400">
          Festival Boards are time-limited. The next one will activate around its holiday window.
        </p>
        <p className="text-zinc-500 text-sm mt-4">
          Next up: 🎆 Independence Day Board (July 1–5 PT)
        </p>
      </div>
    );
  }

  const { board, state, today_pt } = data;
  const isGauntlet = board.mode === "gauntlet";
  const stampsHave = (state.stamps_collected || []).length;
  const stampsNeed = board.stamps?.stamps_for_boss || 0;
  const bossReady = !isGauntlet && stampsHave >= stampsNeed && !state.boss_defeated;
  const grandReady = !isGauntlet && state.boss_defeated && !state.grand_prize_claimed;
  const stuckOnTile = state.stuck_on_tile;
  const summitReached = state.summit_reached;
  const hardLocked = isGauntlet && state.free_rolls_remaining <= 0 && !summitReached;
  const paidCurrencyIcon = board.rolls?.paid_currency === "omni_dollars" ? "💵" : board.currency?.icon;
  const paidCurrencyLabel = board.rolls?.paid_currency === "omni_dollars" ? "Specialized Roll" : "Paid Roll";

  return (
    <div className="festival-board max-w-6xl mx-auto p-4 sm:p-6"
         data-testid="festival-board"
         style={{
           background: board.theme.background_gradient,
           minHeight: "100vh",
         }}>
      {/* ── Header / Currency ── */}
      <div className="mb-6 text-center">
        <div className="text-5xl mb-2">{board.icon}</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-wide"
            style={{ color: board.theme.highlight }}
            data-testid="festival-board-title">
          {board.name}
        </h1>
        <p className="text-sm opacity-80 mt-2" style={{ color: board.theme.highlight }}>{board.blurb}</p>
        <div className="text-xs opacity-60 mt-1" style={{ color: board.theme.highlight }}>
          PT today: {today_pt}
        </div>
        {isOwner && (board.loot_table?.length || 0) > 0 && (
          <div className="mt-3">
            <Link
              to="/owner/loot-tuner"
              data-testid="festival-owner-tune-loot-link"
              className="inline-block px-3 py-1 text-[10px] uppercase tracking-widest font-bold border border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-zinc-900 transition rounded">
              🎁 Tune Loot Table
            </Link>
          </div>
        )}
      </div>

      {/* ── Summit Cleared Banner ── */}
      {summitReached && (
        <div className="mb-6 p-4 rounded-lg text-center border-2 border-amber-300"
             data-testid="festival-summit-cleared"
             style={{ background: "linear-gradient(90deg, rgba(251,191,36,0.3), rgba(220,38,38,0.3))" }}>
          <div className="text-4xl mb-1">🏆</div>
          <div className="text-2xl font-bold text-amber-100">Summit Cleared!</div>
          <div className="text-sm text-amber-100/80 mt-1">
            Run #{state.summit_runs_completed || 1} · Come back tomorrow to climb again.
          </div>
        </div>
      )}

      {/* ── Hard Lock Banner ── */}
      {hardLocked && (
        <div className="mb-6 p-4 rounded-lg text-center border-2 border-rose-500"
             data-testid="festival-hard-locked"
             style={{ background: "rgba(127,29,29,0.4)" }}>
          <div className="text-3xl mb-1">💀</div>
          <div className="text-xl font-bold text-rose-200">Out of Rolls — Hard Locked</div>
          <div className="text-sm text-rose-200/80 mt-1">
            The board is locked until midnight PT. Tomorrow you start fresh at Tile 0.
          </div>
        </div>
      )}

      {/* ── Stats Row ── */}
      <div className={`grid grid-cols-2 ${isGauntlet ? "md:grid-cols-4" : "md:grid-cols-5"} gap-3 mb-6`}>
        {isGauntlet ? (
          <>
            <StatPill icon="🎲" label="Rolls Left" value={`${state.free_rolls_remaining}/${board.rolls.free_per_day}`} testid="fest-free-rolls" />
            <StatPill icon="💵" label={`Specialized (${board.rolls.max_paid_per_day - state.paid_rolls_used_today} left)`} value={`${state.paid_rolls_used_today}/${board.rolls.max_paid_per_day}`} testid="fest-paid-rolls" />
            <StatPill icon="📍" label="Position" value={`Tile ${state.position || 0}`} testid="fest-position" />
            <StatPill icon="🏆" label="Summit Runs" value={state.summit_runs_completed || 0} testid="fest-summit-runs" />
          </>
        ) : (
          <>
            <StatPill icon={board.currency.icon} label={board.currency.name} value={state.currency} testid="fest-currency" />
            <StatPill icon="🎲" label="Free Rolls" value={`${state.free_rolls_remaining}/${board.rolls.free_per_day}`} testid="fest-free-rolls" />
            <StatPill icon="⭐" label="Stamps" value={`${stampsHave}/${stampsNeed}`} testid="fest-stamps" />
            <StatPill icon="🔁" label="Laps" value={state.laps_completed || 0} testid="fest-laps" />
            <StatPill icon="📍" label="Position" value={`Tile ${state.position || 0}`} testid="fest-position" />
          </>
        )}
      </div>

      {/* ── Action bar ── */}
      <div className="flex flex-wrap gap-3 justify-center mb-6">
        <button
          onClick={() => handleRoll(false)}
          disabled={rolling || state.free_rolls_remaining <= 0 || branchPrompt || fightPrompt || stuckOnTile || summitReached}
          data-testid="festival-roll-free-btn"
          className="px-5 py-3 rounded-lg font-bold text-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed transition transform hover:scale-105"
          style={{ background: board.theme.accent }}
        >
          🎲 Roll Free ({state.free_rolls_remaining} left)
        </button>
        <button
          onClick={() => handleRoll(true)}
          disabled={rolling || state.paid_rolls_used_today >= board.rolls.max_paid_per_day || branchPrompt || fightPrompt || stuckOnTile || summitReached}
          data-testid="festival-roll-paid-btn"
          className="px-5 py-3 rounded-lg font-bold border-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
          style={{ borderColor: board.theme.accent, color: board.theme.highlight }}
        >
          {paidCurrencyIcon} {paidCurrencyLabel} · {board.rolls.paid_cost} {paidCurrencyIcon}
        </button>
        {bossReady && (
          <button
            onClick={handleFightBoss}
            data-testid="festival-fight-boss-btn"
            className="px-5 py-3 rounded-lg font-bold text-white animate-pulse"
            style={{ background: board.theme.secondary }}
          >
            🐺 Fight {board.boss.name} ({board.boss.icon} Lv {board.boss.lv})
          </button>
        )}
        {grandReady && (
          <button
            onClick={handleClaimGrand}
            data-testid="festival-claim-grand-btn"
            className="px-5 py-3 rounded-lg font-bold text-zinc-900 animate-pulse"
            style={{ background: "linear-gradient(90deg, #FBBF24, #F59E0B, #DC2626)" }}
          >
            🏆 Claim Grand Prize
          </button>
        )}
      </div>

      {/* ── Last Roll Display ── */}
      {lastRoll && lastRoll.tile && (
        <div className="mb-6 p-4 rounded-lg text-center"
             data-testid="festival-last-roll"
             style={{ background: "rgba(0,0,0,0.4)", color: board.theme.highlight }}>
          <div className="text-sm opacity-70">Last Roll · 🎲 {lastRoll.roll}</div>
          <div className="text-2xl font-bold my-1">
            {lastRoll.tile.icon} {lastRoll.tile.label}
          </div>
          {lastRoll.rewards?.length > 0 && (
            <div className="text-sm opacity-90">
              {lastRoll.rewards.map((r, i) => (
                <span key={i} className="inline-block mx-2">
                  {r.icon} {r.name} {r.qty > 1 ? `×${r.qty}` : ""}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── The Board (36 tiles in a flowing path) ── */}
      <BoardGrid board={board} position={state.position} stamps={state.stamps_collected || []} />

      {/* ── Branch Picker Modal ── */}
      {branchPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
             data-testid="festival-branch-modal">
          <div className="max-w-md w-full rounded-xl p-6 text-center"
               style={{ background: "linear-gradient(135deg, #1E293B, #0F172A)", color: "#fff" }}>
            <div className="text-4xl mb-2">🌟</div>
            <h3 className="text-2xl font-bold mb-1">Choose Your Path</h3>
            <p className="text-sm opacity-70 mb-4">You stand at a fork. Choose wisely.</p>
            <div className="flex flex-col gap-3">
              {branchPrompt.options?.map((o) => (
                <button key={o.to}
                        onClick={() => handlePickBranch(o.to)}
                        data-testid={`festival-branch-pick-${o.to}`}
                        className="px-4 py-3 rounded-lg font-bold border-2 border-cyan-400 hover:bg-cyan-900/30 transition">
                  {o.icon} {o.label} <span className="opacity-60 text-sm">(→ Tile {o.to})</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Fight Prompt Modal ── */}
      {fightPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
             data-testid="festival-fight-modal">
          <div className="max-w-md w-full rounded-xl p-6 text-center"
               style={{ background: "linear-gradient(135deg, #7F1D1D, #1E293B)", color: "#fff" }}>
            <div className="text-5xl mb-2">{fightPrompt.fight.icon || "⚔️"}</div>
            <h3 className="text-2xl font-bold mb-1">{fightPrompt.fight.label || "Battle Tile!"}</h3>
            {fightPrompt.fight.type === "mirror_match" ? (
              <>
                <p className="text-sm opacity-90 mb-2">
                  Your mirror image steps forward. Enemy stats: <span className="text-amber-300 font-bold">
                  +{Math.round(((fightPrompt.fight.enemy_stat_multiplier || 1.10) - 1) * 100)}%</span>
                </p>
                <p className="text-xs opacity-60 mb-4">
                  Must WIN to advance · Loss consumes 1 roll · HP resets to 100 on this tile
                </p>
                {fightPrompt.lost && (
                  <p className="text-xs text-rose-300 font-bold mb-3">
                    💀 Defeated! HP restored to 100. {fightPrompt.rolls_remaining} rolls left.
                  </p>
                )}
                <div className="flex gap-3 justify-center">
                  <button onClick={() => handleFightResult(true)}
                          data-testid="festival-fight-win-btn"
                          className="px-5 py-2 rounded-lg font-bold bg-rose-600 hover:bg-rose-500">
                    🔥 {fightPrompt.lost ? "Retry & Win" : "Fight & Win"}
                  </button>
                  <button onClick={() => handleFightResult(false)}
                          data-testid="festival-fight-flee-btn"
                          className="px-5 py-2 rounded-lg font-bold bg-zinc-700 hover:bg-zinc-600">
                    💢 Lose (Test)
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm opacity-70 mb-4">
                  A {fightPrompt.fight.boss_id || "challenger"} blocks the path. Will you fight?
                </p>
                <div className="flex gap-3 justify-center">
                  <button onClick={() => handleFightResult(true)}
                          data-testid="festival-fight-win-btn"
                          className="px-5 py-2 rounded-lg font-bold bg-rose-600 hover:bg-rose-500">
                    ⚔️ Fight & Win
                  </button>
                  <button onClick={() => handleFightResult(false)}
                          data-testid="festival-fight-flee-btn"
                          className="px-5 py-2 rounded-lg font-bold bg-zinc-700 hover:bg-zinc-600">
                    🏃 Flee
                  </button>
                </div>
              </>
            )}
            <p className="text-xs opacity-50 mt-3">
              (Phase 1 ships auto-resolution. Combat engine integration in Phase 2.)
            </p>
          </div>
        </div>
      )}

      {/* ── History ── */}
      {state.history?.length > 0 && (
        <div className="mt-8 p-4 rounded-lg" style={{ background: "rgba(0,0,0,0.4)", color: board.theme.highlight }}>
          <h3 className="font-bold mb-2 text-lg">📜 Recent Rolls</h3>
          <div className="space-y-1 text-sm max-h-48 overflow-y-auto" data-testid="festival-history">
            {state.history.slice(0, 10).map((h, i) => (
              <div key={i} className="opacity-80 flex items-center gap-2 flex-wrap">
                <span className="opacity-50">🎲 {h.roll}</span>
                <span>→</span>
                <span className="font-medium">{h.tile_icon} Tile {h.tile_id} · {h.tile_label}</span>
                {h.rewards?.length > 0 && (
                  <span className="opacity-70 text-xs">
                    [{h.rewards.map(r => `${r.icon}${r.qty > 1 ? r.qty : ""}`).join(" ")}]
                  </span>
                )}
                {h.laps_added > 0 && <span className="text-amber-300 text-xs">🔁 +{h.laps_added} lap</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatPill({ icon, label, value, testid }) {
  return (
    <div className="rounded-lg p-3 text-center backdrop-blur-sm border border-white/10"
         style={{ background: "rgba(0,0,0,0.4)" }}
         data-testid={testid}>
      <div className="text-2xl">{icon}</div>
      <div className="text-xs opacity-60 text-white">{label}</div>
      <div className="text-lg font-bold text-white">{value}</div>
    </div>
  );
}

function BoardGrid({ board, position, stamps }) {
  // Layout 36 tiles in a snaking grid (6 cols × 6 rows).
  // Top row L→R, second row R→L, etc., to make the path feel like a loop.
  const rows = useMemo(() => {
    const tilesById = Object.fromEntries(board.tiles.map(t => [t.id, t]));
    const out = [];
    for (let r = 0; r < 6; r++) {
      const start = r * 6 + 1;
      const ids = [];
      for (let c = 0; c < 6; c++) ids.push(start + c);
      if (r % 2 === 1) ids.reverse();
      out.push(ids.map(id => tilesById[id]).filter(Boolean));
    }
    return out;
  }, [board]);

  return (
    <div className="rounded-xl p-3" style={{ background: "rgba(0,0,0,0.35)" }} data-testid="festival-grid">
      {rows.map((row, ri) => (
        <div key={ri} className="grid grid-cols-6 gap-2 mb-2">
          {row.map((t) => (
            <Tile key={t.id} tile={t} isHere={position === t.id} stamped={stamps.includes(t.id)} theme={board.theme} />
          ))}
        </div>
      ))}
    </div>
  );
}

function Tile({ tile, isHere, stamped, theme }) {
  const sty = TILE_TYPE_STYLES[tile.type] || TILE_TYPE_STYLES.loot;
  return (
    <div className={`relative rounded-lg p-2 border-2 ${sty.border} bg-gradient-to-br ${sty.bg} text-center transition transform ${isHere ? "scale-110 ring-2 ring-white shadow-2xl z-10" : ""}`}
         data-testid={`festival-tile-${tile.id}`}>
      <div className="text-xs opacity-60 text-white">#{tile.id}</div>
      <div className="text-2xl">{tile.icon}</div>
      <div className="text-[10px] text-white opacity-80 leading-tight mt-1 truncate" title={tile.label}>
        {tile.label}
      </div>
      {stamped && <div className="absolute -top-1 -right-1 text-base">⭐</div>}
      {isHere && <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-2xl drop-shadow-lg" data-testid="festival-token">🐦‍🔥</div>}
    </div>
  );
}
